/**
 * useCustomerReferral — Data hook for the Customer Referral Dashboard.
 *
 * Generates or retrieves the user's referral code, and fetches
 * referral status (invited friends, their signup/subscription status).
 *
 * Task: NocoDB #1554 — Referral dashboard + share components in app
 * Epic: #1529 — DD Affiliate + Customer Referral Program
 *
 * Migration status: DTO contract (2026-04-18, Wave 4B)
 * ────────────────────────────────────────────────
 * Reads (`get-referral`) and the generateCode mutation (`referral-code`) use
 * `fetchJsonValidated(<Dto>, ...)` so server responses are validated against
 * the shared Zod contract. Backend/DB renames can no longer drift the UI
 * silently — validation fails fast instead.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import { CustomerReferralDto, ReferralCodeResultDto } from '@dramadetector/shared';
import type { ReferralRow } from '@dramadetector/shared';
import { buildReferralUrl } from '../lib/url';
import { copyReferralUrl, shareReferralUrl } from '../lib/referral-sharing';
import { getEdgeFunctionUrl, getAuthHeaders } from '../lib/api-url';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface ReferralStatus {
  id: string;
  referredEmail: string | null;
  status: 'pending' | 'signed_up' | 'subscribed' | 'rewarded' | 'expired';
  createdAt: string;
  /** Pre-formatted date string for display (avoids Date allocation in render) */
  displayDate: string;
}

export interface UseCustomerReferralReturn {
  /** User's referral code */
  referralCode: string | null;
  /** Short URL for sharing */
  shortUrl: string | null;
  /** List of referrals made by this user */
  referrals: ReferralStatus[];
  /** Total count of referrals */
  totalReferrals: number;
  /** Count of successful conversions */
  conversions: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Generate referral code (if not already generated) */
  generateCode: () => void;
  /** Whether code generation is in progress */
  isGenerating: boolean;
  /** Copy referral link to clipboard */
  copyReferralLink: () => Promise<void>;
  /** Share referral link via native share sheet */
  shareReferralLink: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const customerReferralKeys = {
  all: ['customer-referral'] as const,
  code: (userId: string | null) =>
    [...customerReferralKeys.all, 'code', userId] as const,
  referrals: (userId: string | null) =>
    [...customerReferralKeys.all, 'referrals', userId] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

function getFirstError(...errors: unknown[]): Error | null {
  for (const err of errors) {
    if (err instanceof Error) return err;
  }
  return null;
}

export function useCustomerReferral(): UseCustomerReferralReturn {
  const { user, accessToken } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  // Fetch referral data (code + referrals) in a single call
  const referralQuery = useQuery({
    queryKey: customerReferralKeys.code(userId),
    queryFn: async () => {
      if (!userId) return { code: null, referrals: [] as ReferralStatus[] };

      const dto = await fetchJsonValidated(
        CustomerReferralDto,
        getEdgeFunctionUrl('get-referral'),
      );

      const referrals: ReferralStatus[] = dto.referrals.map((r: ReferralRow) => ({
        id: r.id,
        referredEmail: r.referred_email,
        status: r.status,
        createdAt: r.created_at,
        displayDate: new Date(r.created_at).toLocaleDateString(),
      }));

      return { code: dto.code ?? null, referrals };
    },
    staleTime: 1000 * 60 * 2,
    enabled: !!accessToken && !!userId,
  });

  // Generate code mutation — validated DTO
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');

      const dto = await fetchJsonValidated(
        ReferralCodeResultDto,
        getEdgeFunctionUrl('referral-code'),
        {
          method: 'POST',
          headers: { ...getAuthHeaders(accessToken) },
          body: JSON.stringify({ code_type: 'customer' }),
        },
      );
      return dto.code;
    },
    onSuccess: (code) => {
      queryClient.setQueryData(
        customerReferralKeys.code(userId),
        (prev: { code: string | null; referrals: ReferralStatus[] } | undefined) => ({
          code,
          referrals: prev?.referrals ?? [],
        }),
      );
    },
  });

  const referralCode = referralQuery.data?.code ?? generateMutation.data ?? null;
  const referrals = referralQuery.data?.referrals ?? [];

  const fullUrl = useMemo(
    () => referralCode ? buildReferralUrl(referralCode) : null,
    [referralCode]
  );

  const conversions = useMemo(
    () => referrals.filter((r) => r.status === 'subscribed' || r.status === 'rewarded').length,
    [referrals]
  );

  const copyReferralLink = useCallback(
    () => copyReferralUrl(fullUrl),
    [fullUrl]
  );

  const shareReferralLink = useCallback(
    () => shareReferralUrl(fullUrl),
    [fullUrl]
  );

  const generateCode = useCallback(() => {
    generateMutation.mutate();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable per React Query docs
  }, [generateMutation.mutate]);

  return {
    referralCode,
    shortUrl: fullUrl,
    referrals,
    totalReferrals: referrals.length,
    conversions,
    isLoading: referralQuery.isLoading,
    error: getFirstError(referralQuery.error),
    generateCode,
    isGenerating: generateMutation.isPending,
    copyReferralLink,
    shareReferralLink,
  };
}
